/** @typedef {import('../index.js').BaseDocData} BaseDocData */
/* eslint-disable no-underscore-dangle */
import * as Yup from 'yup';
import { LotEventTypes } from '../../constants/index.js';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

/**
 * One row of a lot's Activity log, stored in `lots/{id}/events`.
 * Events are written alongside every lot mutation in the same
 * transaction/batch, always through this model. Events are immutable,
 * so `_createdBy` (the repo-wide audit convention) carries both the
 * actor and the event time — the Activity feed orders by
 * `_createdBy.timestamp`.
 */
export const lotEventSchema = Yup.object().shape({
  type: Yup.string().oneOf(Object.values(LotEventTypes)).required('Type is required'),
  /** Quantity change, for `consumed`/`adjusted` events. */
  deltaQty: Yup.number().nullable().default(null),
  reason: Yup.string().nullable().default(null),
  /** Doc IDs of related records — only these keys, all optional. */
  relatedIds: Yup.object().shape({
    travelerId: Yup.string(),
    lotId: Yup.string(),
    inspectionId: Yup.string(),
  }).test(
    // noUnknown() doesn't run on nested schemas outside strict mode.
    'known-keys-only',
    'relatedIds only carries travelerId, lotId, inspectionId',
    (value) => !value || Object.keys(value)
      .every((key) => ['travelerId', 'lotId', 'inspectionId'].includes(key)),
  ).default({}),
  /**
   * Required, like the lot's: a write with a missing user should fail
   * loudly, and the Activity feed orders by _createdBy.timestamp.
   */
  _createdBy: Yup.object().shape({
    uid: Yup.string().required('_createdBy.uid is required'),
    email: Yup.string().email().required('_createdBy.email is required'),
    timestamp: YupFirestoreTimestamp.nullable(),
  }).required('_createdBy is required'),
});

/**
 * @typedef {Yup.InferType<typeof lotEventSchema>} LotEventSchema
 * @typedef {BaseDocData & LotEventSchema} LotEventDocData
 */

export default class LotEvent {
  constructor({
    id, type, deltaQty, reason, relatedIds, _createdBy,
  }) {
    const validatedData = lotEventSchema.validateSync({
      type,
      deltaQty,
      reason,
      relatedIds,
      _createdBy,
    });

    this.id = id || null;
    this.type = validatedData.type;
    this.deltaQty = validatedData.deltaQty ?? null;
    this.reason = validatedData.reason ?? null;
    this.relatedIds = validatedData.relatedIds || {};
    this._createdBy = validatedData._createdBy;
  }

  toFirestore({ serverTimestamp }) {
    return {
      type: this.type,
      deltaQty: this.deltaQty,
      reason: this.reason,
      // Rebuild map fields as plain objects — Firestore rejects the
      // prototype-less objects Yup's cast produces. _createdBy always
      // carries a timestamp: the Activity feed orders by it.
      relatedIds: { ...this.relatedIds },
      _createdBy: {
        uid: this._createdBy.uid,
        email: this._createdBy.email,
        timestamp: this._createdBy.timestamp || serverTimestamp(),
      },
    };
  }

  static fromFirestore(docData) {
    return new LotEvent(docData);
  }
}
